/**
 * Scrolls the window back to the top whenever the route pathname changes.
 *
 * React Router v6 does NOT do this by default — it keeps the previous
 * scroll position, which means clicking a link from deep in a long page
 * (e.g. the footer) lands the user mid-page on the new route, with the
 * header off-screen. That's confusing on long landing pages like /, /cars,
 * /blog/* etc.
 *
 * We intentionally key on `pathname` only (not `search` or `hash`) so:
 *   • In-page anchor jumps (`/foo#section`) still work as expected.
 *   • Query-param-only updates (filters, pagination) don't snap to top.
 *
 * Disables browser-managed scroll restoration on mount because back/forward
 * gestures with React Router's client-side routing don't always restore
 * cleanly — explicit control is more predictable.
 */
import { useEffect } from "react";
import { useLocation } from "react-router-dom";

export default function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      if ("scrollRestoration" in window.history) {
        window.history.scrollRestoration = "manual";
      }
    } catch {
      /* private mode / sandbox — safe to ignore */
    }
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    // `instant` (not "smooth") so the new page never starts mid-scroll.
    // Some Safari versions still don't accept the options object — wrap
    // in try/catch and fall back to the positional API.
    try {
      window.scrollTo({ top: 0, left: 0, behavior: "instant" });
    } catch {
      window.scrollTo(0, 0);
    }
    // Also reset documentElement / body scroll for layouts that scroll
    // a custom container rather than the window itself.
    try {
      if (document?.documentElement) document.documentElement.scrollTop = 0;
      if (document?.body) document.body.scrollTop = 0;
    } catch {
      /* no-op */
    }
  }, [pathname]);

  return null;
}
